import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ContentCopy, ContentPaste, Navigation } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Alert, Box, Button, Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import useDebouncedCallback from '../../../common/hooks/useDebouncedCallback';
import { ConfigContext } from '../../../components';
import { Bundle } from '../Models/Bundle';
import { IComponentModel } from '../Models/IComponentModel';
import { IContainerComponentModel } from '../Models/IContainerComponentModel';
import { ReactBundleModel } from '../Models/ReactBundleModel';
import { DESIGNER_PROTOCOL_VERSION, DesignerMessage, MESSAGE_TYPES, SHARED_COMPONENT_ROOT_TYPE } from '../Types/DesignerMessages';
import { buildBundleComponentUpdatedMessage, buildBundleMetadataUpdatedMessage, buildReactBundleUpdatedMessage } from '../Utils/BundlePreviewDeltaUtils';
import { ComponentPath } from '../Utils/ComponentTreeUtils';
import { buildInteractivePreviewUrl } from '../Utils/InteractivePreviewUrl';
import type { SharedComponentSelectionContext } from './AppArtifactPanel';

type RuntimePreviewFrameProps = {
	view: IContainerComponentModel;
	/** Increment to request a navigation to the current view. Passive syncs (VIEW_ACTIVATED, component clicks) must not increment this. */
	navigationRequestId?: number;
	selectedPath: ComponentPath | null;
	selectedSharedComponentContext?: SharedComponentSelectionContext | null;
	onSelectionChange: (path: ComponentPath, viewId?: string, sharedComponentId?: string, referenceComponentPath?: ComponentPath) => void;
	onComponentUpdate?: (path: ComponentPath, updates: Partial<IComponentModel>) => void;
	bundle?: Bundle;
	onNavigationToView?: (viewId: string) => void;
	onCurrentUrlChange?: (url: string | null) => void;
	navigationCommand?: string | null;
	title?: string;
	routePath?: string;
	currentRuntimeUrl?: string | null;
	onNavigateClick?: (event: React.MouseEvent<HTMLElement>) => void;
	onCopyViewDefinition?: () => void;
	onPasteViewDefinition?: () => void;
	reactBuildReloadRequest?: { buildId: string; reactBundle: ReactBundleModel; requestId: number };
};

/**
 * Live Preview component that embeds app-runtime in an iframe
 * Uses postMessage for communication between app-portal and app-runtime
 * Implements Option 1B from the architecture documentation
 */
export function RuntimePreviewFrame({
	view,
	navigationRequestId = 0,
	selectedPath,
	selectedSharedComponentContext,
	onSelectionChange,
	onComponentUpdate,
	bundle,
	onNavigationToView,
	onCurrentUrlChange,
	navigationCommand,
	title,
	routePath,
	currentRuntimeUrl,
	onNavigateClick,
	onCopyViewDefinition,
	onPasteViewDefinition,
	reactBuildReloadRequest,
}: RuntimePreviewFrameProps) {
	const config = useContext(ConfigContext);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [isReady, setIsReady] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [authenticationRequired, setAuthenticationRequired] = useState(false);
	const [previewKey, setPreviewKey] = useState(0);
	const runtimeOrigin = config.RUNTIME_ORIGIN && config.RUNTIME_ORIGIN !== '*' ? config.RUNTIME_ORIGIN : new URL(config.RUNTIME_URL).origin;
	const interactivePreviewUrl = currentRuntimeUrl && bundle?.Id ? buildInteractivePreviewUrl(currentRuntimeUrl, runtimeOrigin, bundle.Id) : undefined;
	const runtimeSignInUrl = new URL(config.RUNTIME_URL);
	runtimeSignInUrl.searchParams.set('reauth', '1');

	const retryPreview = useCallback(() => {
		setAuthenticationRequired(false);
		setError(null);
		setIsReady(false);
		setPreviewKey((key) => key + 1);
	}, []);

	// Refs that track message-loop guards
	const lastConfirmedViewIdRef = useRef<string | null>(null); // set by COMPONENT_SELECTED, cleared in VIEW_ACTIVATED
	const pendingNavViewIdRef = useRef<string | null>(null); // set when portal sends a nav, cleared on VIEW_ACTIVATED confirmation

	// Keep refs in sync with the latest props so the stable message listener can read them
	// without needing to be re-registered on every prop change.
	const bundleRef = useRef(bundle);
	const lastSentBundleRef = useRef<Bundle | undefined>(bundle);
	const viewRef = useRef(view);
	const lastComponentSelectionRef = useRef<{
		selectedPath: ComponentPath;
		selectedSharedComponentContext: SharedComponentSelectionContext | null;
	} | null>(null);
	useEffect(() => {
		bundleRef.current = bundle;
	}, [bundle]);
	useEffect(() => {
		viewRef.current = view;
	}, [view]);
	useEffect(() => {
		if (selectedPath && selectedPath.length > 0) {
			lastComponentSelectionRef.current = {
				selectedPath,
				selectedSharedComponentContext: selectedSharedComponentContext ?? null,
			};
		}
	}, [selectedPath, selectedSharedComponentContext]);

	// Build the appropriate navigation message for the given view and bundle.
	// SharedComponents, dialogs, parameterised routes, and routes-less views all use RENDER_VIEW
	// so that app-runtime navigates to /designer-preview rather than the real route (which would
	// trigger spurious VIEW_ACTIVATED messages and restart the loop).
	const buildNavMessage = useCallback((targetView: IContainerComponentModel, targetBundle: Bundle): DesignerMessage => {
		const fullView = targetBundle.Views.find((v) => v.Id === targetView.Id);
		const isSharedComponent = !fullView || targetView.Type === SHARED_COMPONENT_ROOT_TYPE;

		if (isSharedComponent || !fullView.RoutePath || fullView.RoutePath.includes(':') || !!fullView.Dialog) {
			return {
				type: MESSAGE_TYPES.RENDER_VIEW,
				payload: { view: targetView, editMode: true, selectedPath: null },
			};
		}

		return {
			type: MESSAGE_TYPES.NAVIGATE_TO_VIEW,
			payload: { viewId: targetView.Id, routePath: fullView.RoutePath },
		};
	}, []);

	// Handler for back navigation
	const handleNavigateBack = () => {
		if (!isReady || !iframeRef.current?.contentWindow) return;
		iframeRef.current.contentWindow.postMessage({ type: MESSAGE_TYPES.NAVIGATE_BACK }, runtimeOrigin);
	};

	const sendFullBundleUpdate = useCallback(
		(bundleToSend: Bundle) => {
			if (!isReady || !iframeRef.current?.contentWindow) return;
			iframeRef.current.contentWindow.postMessage({ type: MESSAGE_TYPES.SET_BUNDLE, payload: { bundle: bundleToSend } }, runtimeOrigin);
			lastSentBundleRef.current = bundleToSend;
		},
		[runtimeOrigin, isReady],
	);

	// Debounced bundle update sender (100ms delay to batch rapid property changes)
	const sendBundleUpdate = useDebouncedCallback((bundleToSend: Bundle) => {
		if (!isReady || !iframeRef.current?.contentWindow) return;

		const previousSelection = lastComponentSelectionRef.current;
		const reactBundleUpdateMessage = buildReactBundleUpdatedMessage(lastSentBundleRef.current, bundleToSend);
		if (reactBundleUpdateMessage) {
			iframeRef.current.contentWindow.postMessage(reactBundleUpdateMessage, runtimeOrigin);
			lastSentBundleRef.current = bundleToSend;
			return;
		}

		const componentUpdateMessage = buildBundleComponentUpdatedMessage({
			previousBundle: lastSentBundleRef.current,
			nextBundle: bundleToSend,
			currentView: viewRef.current,
			selectedPath: selectedPath ?? previousSelection?.selectedPath ?? null,
			selectedSharedComponentContext: selectedPath ? selectedSharedComponentContext : previousSelection?.selectedSharedComponentContext,
		});

		if (componentUpdateMessage) {
			iframeRef.current.contentWindow.postMessage(componentUpdateMessage, runtimeOrigin);
			lastSentBundleRef.current = bundleToSend;
			if (!selectedPath) {
				lastComponentSelectionRef.current = null;
			}
			return;
		}

		const metadataUpdateMessage = buildBundleMetadataUpdatedMessage(lastSentBundleRef.current, bundleToSend);
		if (metadataUpdateMessage) {
			iframeRef.current.contentWindow.postMessage(metadataUpdateMessage, runtimeOrigin);
			lastSentBundleRef.current = bundleToSend;
			return;
		}

		sendFullBundleUpdate(bundleToSend);
	}, 100);

	// Send bundle updates to runtime whenever bundle changes.
	// Debouncing prevents rapid re-renders in runtime during property changes.
	useEffect(() => {
		if (!isReady || !bundle) return;
		sendBundleUpdate(bundle);
	}, [bundle, isReady, sendBundleUpdate]);

	useEffect(() => {
		if (!isReady || !iframeRef.current?.contentWindow || !reactBuildReloadRequest) return;
		iframeRef.current.contentWindow.postMessage(
			{
				type: MESSAGE_TYPES.REACT_BUILD_UPDATED,
				payload: { buildId: reactBuildReloadRequest.buildId, reactBundle: reactBuildReloadRequest.reactBundle },
			} satisfies DesignerMessage,
			runtimeOrigin,
		);
	}, [runtimeOrigin, isReady, reactBuildReloadRequest]);

	// Send navigation message only when navigationRequestId increments (explicit user Artifact
	// selection). Passive view syncs (VIEW_ACTIVATED, component clicks) never cause a nav message
	// because they do not increment the counter. View/bundle are read through refs so this effect
	// does not need them in its dependency array.
	useEffect(() => {
		if (!isReady || !iframeRef.current?.contentWindow) return;
		const currentView = viewRef.current;
		const currentBundle = bundleRef.current;
		if (!currentBundle) return;

		const message = buildNavMessage(currentView, currentBundle);
		// Track the in-flight nav so VIEW_ACTIVATED for the old view doesn’t revert Artifacts.
		// RENDER_VIEW also confirms its view, including newly created views without a route.
		pendingNavViewIdRef.current = currentView.Id;
		iframeRef.current.contentWindow.postMessage(message, runtimeOrigin);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigationRequestId, isReady, runtimeOrigin]);

	// Update selection when selectedPath changes (sent separately from navigation)
	useEffect(() => {
		if (!isReady || !iframeRef.current?.contentWindow) return;
		iframeRef.current.contentWindow.postMessage({ type: MESSAGE_TYPES.UPDATE_SELECTION, payload: { path: selectedPath || [] } }, runtimeOrigin);
	}, [selectedPath, isReady, runtimeOrigin]);

	// Forward explicit navigation commands from the parent
	useEffect(() => {
		if (!isReady || !iframeRef.current?.contentWindow || !navigationCommand) return;
		// An explicit URL replaces any older artifact navigation awaiting confirmation.
		pendingNavViewIdRef.current = null;
		lastConfirmedViewIdRef.current = null;
		iframeRef.current.contentWindow.postMessage({ type: MESSAGE_TYPES.NAVIGATE_TO_PATH, payload: { path: navigationCommand } }, runtimeOrigin);
	}, [navigationCommand, isReady, runtimeOrigin]);

	// Stable message listener — reads bundle/view through refs so it never needs to be
	// re-registered when those values change, avoiding an event listener thrash on every edit.
	useEffect(() => {
		const origin = runtimeOrigin;

		const handleMessage = (event: MessageEvent<DesignerMessage>) => {
			if (!iframeRef.current?.contentWindow || event.source !== iframeRef.current.contentWindow) return;
			if (event.origin !== origin) return;

			const message = event.data;
			if (!message || typeof message !== 'object') return;

			switch (message.type) {
				case MESSAGE_TYPES.AUTH_STATE_CHANGED:
					if (typeof message.payload?.isAuthenticated === 'boolean') {
						retryPreview();
					}
					break;
				case MESSAGE_TYPES.AUTH_REQUIRED:
					setError(null);
					setAuthenticationRequired(true);
					break;
				case MESSAGE_TYPES.IFRAME_READY: {
					const runtimeProtocolVersion = message.payload?.protocolVersion;
					if (runtimeProtocolVersion !== DESIGNER_PROTOCOL_VERSION) {
						setIsReady(false);
						setError(`Runtime designer protocol ${runtimeProtocolVersion ?? 'unknown'} is incompatible with portal protocol ${DESIGNER_PROTOCOL_VERSION}.`);
						return;
					}

					setError(null);
					lastConfirmedViewIdRef.current = null;
					pendingNavViewIdRef.current = null;

					const win = iframeRef.current?.contentWindow;
					const currentBundle = bundleRef.current;
					const currentView = viewRef.current;

					if (!win || !currentBundle) {
						// First load — just mark ready and let the effects handle it
						setIsReady(true);
						return;
					}

					// Always imperatively re-send bundle + navigation on IFRAME_READY.
					// DesignerModeProvider lives *inside* the React Router, so every time the
					// router is recreated (e.g. a new view is added to the bundle) the provider
					// remounts and fires IFRAME_READY again — even though isReady is already true.
					// Without this imperative path the effects below won't re-run (no state change)
					// and the iframe would be left showing "Waiting for component model".
					win.postMessage({ type: MESSAGE_TYPES.SET_BUNDLE, payload: { bundle: currentBundle } }, origin);
					lastSentBundleRef.current = currentBundle;
					const navMsg = buildNavMessage(currentView, currentBundle);
					pendingNavViewIdRef.current = currentView.Id;
					win.postMessage(navMsg, origin);

					setIsReady(true);
					break;
				}

				case MESSAGE_TYPES.COMPONENT_SELECTED: {
					// If the click came from a view the portal isn't currently tracking, mark it so
					// the VIEW_ACTIVATED confirmation is skipped (avoiding a spurious path clear).
					if (message.payload.viewId && message.payload.viewId !== viewRef.current.Id) {
						lastConfirmedViewIdRef.current = message.payload.viewId;
					}
					onSelectionChange(message.payload.path, message.payload.viewId, message.payload.sharedComponentId, message.payload.referenceComponentPath);
					break;
				}

				case MESSAGE_TYPES.COMPONENT_UPDATED:
					onComponentUpdate?.(message.payload.path, message.payload.updates);
					break;

				case MESSAGE_TYPES.ADD_COMPONENT:
					onSelectionChange(message.payload.parentPath);
					break;

				case MESSAGE_TYPES.VIEW_ACTIVATED: {
					const { viewId, currentPath } = message.payload;
					const currentView = viewRef.current;

					// Ignore stale activations before updating either selection or the preview URL.
					if (pendingNavViewIdRef.current !== null && viewId !== pendingNavViewIdRef.current) break;
					pendingNavViewIdRef.current = null;

					// SharedComponents confirm /designer-preview without selecting a View artifact.
					if (currentView.Type === SHARED_COMPONENT_ROOT_TYPE) {
						onCurrentUrlChange?.(config.RUNTIME_URL + currentPath);
						break;
					}

					onCurrentUrlChange?.(config.RUNTIME_URL + currentPath);

					// COMPONENT_SELECTED already handled this view transition — calling
					// onNavigationToView here would clear the component path unnecessarily.
					if (lastConfirmedViewIdRef.current === viewId) {
						lastConfirmedViewIdRef.current = null;
						break;
					}

					// Passive sync — runtime navigated via user interaction; update Artifacts selection.
					onNavigationToView?.(viewId);
					break;
				}
			}
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [runtimeOrigin, config.RUNTIME_URL, onSelectionChange, onComponentUpdate, onNavigationToView, onCurrentUrlChange, buildNavMessage, retryPreview]);

	// Handle iframe load errors
	const handleIframeError = () => {
		setError(`Failed to load runtime from ${config.RUNTIME_URL}. Make sure app-runtime is running.`);
		setIsReady(false);
	};

	return (
		<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
			{/* Header with title, route, and controls */}
			<Box sx={{ p: 2, pb: 1.5, flexShrink: 0 }}>
				<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
					{/* Title and Route */}
					{(title || routePath) && (
						<Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
							{title && <Typography variant="subtitle2">{title}</Typography>}
							{routePath && <Chip label={routePath} size="small" variant="outlined" />}
						</Stack>
					)}

					{/* Back Button */}
					<Tooltip title="Navigate Back">
						<span>
							<IconButton size="small" onClick={handleNavigateBack} disabled={!isReady}>
								<ArrowBackIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>

					{/* Navigate To Button */}
					{onNavigateClick && (
						<Tooltip title="Navigate to URL">
							<IconButton size="small" onClick={onNavigateClick}>
								<Navigation fontSize="small" />
							</IconButton>
						</Tooltip>
					)}

					{onCopyViewDefinition && (
						<Tooltip title="Copy view definition">
							<IconButton size="small" onClick={onCopyViewDefinition} aria-label="Copy view definition">
								<ContentCopy fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					{onPasteViewDefinition && (
						<Tooltip title="Paste and replace view definition">
							<IconButton size="small" onClick={onPasteViewDefinition} aria-label="Paste and replace view definition">
								<ContentPaste fontSize="small" />
							</IconButton>
						</Tooltip>
					)}

					{interactivePreviewUrl && view.Type !== SHARED_COMPONENT_ROOT_TYPE && (
						<Tooltip title="Run the saved App with runtime sign-in in your selected runtime environment. Creates real chat/file resources; save your changes first.">
							<Button
								variant="outlined"
								size="small"
								startIcon={<PlayArrowIcon />}
								component="a"
								href={interactivePreviewUrl}
								aria-label="Run preview"
								target="_blank"
								rel="noopener noreferrer"
							>
								Run preview
							</Button>
						</Tooltip>
					)}
					{/* Open Page Button */}
					{currentRuntimeUrl && view.Type !== SHARED_COMPONENT_ROOT_TYPE && (
						<Button
							variant="outlined"
							size="small"
							startIcon={<OpenInNewIcon />}
							component="a"
							href={currentRuntimeUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							Open Page
						</Button>
					)}
				</Stack>
				<Divider />
			</Box>

			{/* Loading indicator */}
			{!isReady && !error && !authenticationRequired && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'background.default',
						zIndex: 1,
					}}
				>
					<Typography variant="body2" color="text.secondary">
						Loading runtime preview from {config.RUNTIME_URL}...
					</Typography>
				</Box>
			)}

			{/* Error message */}
			{error && (
				<Box sx={{ p: 3 }}>
					<Alert severity="error">
						<Typography variant="body2">{error}</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
							Configured URL: {config.RUNTIME_URL}
						</Typography>
					</Alert>
				</Box>
			)}
			{authenticationRequired && (
				<Box sx={{ p: 2 }}>
					<Alert
						severity="warning"
						action={
							<Stack direction="row" spacing={1}>
								<Button color="inherit" size="small" component="a" href={runtimeSignInUrl.toString()} target="_blank" rel="noopener noreferrer">
									Sign in to runtime
								</Button>
								<Button color="inherit" size="small" onClick={retryPreview}>
									Retry preview
								</Button>
							</Stack>
						}
					>
						The app runtime needs you to sign in. Use the floating Sign in control in the preview, or sign in in a new tab and retry.
					</Alert>
				</Box>
			)}

			{/* iframe */}
			<Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
				<iframe
					key={previewKey}
					ref={iframeRef}
					src={`${config.RUNTIME_URL}/`}
					style={{
						width: '100%',
						height: '100%',
						border: 'none',
						backgroundColor: '#fff',
						display: isReady ? 'block' : 'none',
					}}
					title="Runtime Preview"
					sandbox="allow-scripts allow-same-origin allow-clipboard-write allow-popups allow-popups-to-escape-sandbox"
					allow="clipboard-write"
					onError={handleIframeError}
				/>
			</Box>
		</Box>
	);
}
